<template>
  <WdsModal :show :title="$t('Update Webhook')" @cancel="show = false">
    <WebhookModalForm
      v-model:input="input"
      v-model:active-key="activeKey"
      :loading
      :contains-secret="!!webhookExtracted?.secret"
      :contains-client-secret="!!webhookExtracted?.clientSecret"
      :validation-errors="validationErrors"
      data-test="modal-update-webhook"
    />
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton :disabled="loading" @click="show = false">{{ $t('Cancel') }}</WdsButton>
        <WdsStack gap-md justify-center align-center class="wds-footer-action-buttons">
          <WdsToggle v-model:checked="input.isActive" data-test="modal-webhook-active">
            {{ $t('Set as active') }}
          </WdsToggle>
          <WdsButton
            primary
            :disabled="loading || !hasChanged"
            data-test="modal-webhook-submit"
            @click="submit"
          >
            {{ $t('Confirm') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { WdsButton, WdsModal, WdsStack, WdsToggle } from '@worksome/wds-components'
import { computed, reactive, Ref, ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { required, requiredIf, url } from '@vuelidate/validators'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { updateWebhookMutation } from '@/components/Integrations/Webhooks/graphql/mutations'
import NetworkError from '@/utils/notification/NetworkError'
import { FragmentType, useFragment } from '@/gql'
import { WebhookFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import { uuid } from '@/utils/tools'
import WebhookModalForm from '@/components/Integrations/Webhooks/WebhookModalForm.vue'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'

const show = defineModel<boolean>('show')
const webhook = defineModel<FragmentType<typeof WebhookFragment> | null>('webhook')
const webhookExtracted = computed(() => useFragment(WebhookFragment, webhook.value))

const clientId = 'publicApi'
const isSecretHidden = (secret) => secret === '***************' || secret === ''

interface UpdateWebhookInput {
  id: string
  title: string
  description?: string | null
  url: string
  secret?: string | null
  isActive: boolean
  clientId?: string | null
  clientSecret?: string | null
  clientUrl?: string | null
}

const input = reactive<UpdateWebhookInput>({
  id: uuid(),
  title: '',
  description: null,
  url: '',
  secret: null,
  isActive: false,
  clientId: null,
  clientSecret: null,
  clientUrl: null,
})

watch(webhook, (value) => {
  if (!value) return
  const hook = useFragment(WebhookFragment, value)
  // Here we are filtering out a set of elements that will break our input when we submit
  const { __typename, createdAt, updatedAt, ...inputHook } = hook
  Object.assign(input, inputHook)
})

const hasChanged = computed(() => {
  const hook = useFragment(WebhookFragment, webhook.value)
  if (!hook) return false
  const valueChange = Object.keys(input)
    .filter((key) => key !== 'clientSecret' && key !== 'secret')
    .some((key) => input[key] !== hook[key])

  return (
    valueChange ||
    (input.secret !== null && input.secret !== '') ||
    (input.clientSecret !== null && input.clientSecret !== '')
  )
})

const {
  loading,
  mutate: updateWebhook,
  error,
} = useMutation(updateWebhookMutation, () => ({
  clientId,
}))

const activeKey: Ref<'SECRET_KEY' | 'OAUTH'> = ref('SECRET_KEY')

const clientSecretRequire = () =>
  activeKey.value === 'OAUTH' && isSecretHidden(webhookExtracted.value?.secret)
const clientRequire = () => activeKey.value === 'OAUTH'
const secretRequire = () =>
  activeKey.value === 'SECRET_KEY' && isSecretHidden(webhookExtracted.value?.clientSecret)

const apollo = useApolloError(error)
const vulidate = useCustomVuelidate(
  {
    title: { required },
    url: { required, url },
    clientUrl: { url, requiredIfClient: requiredIf(clientRequire) },
    clientSecret: { requiredIfClient: requiredIf(clientSecretRequire) },
    clientId: { requiredIfClient: requiredIf(clientRequire) },
    secret: { requiredIfSecret: requiredIf(secretRequire) },
  },
  input,
)

const validationErrors = useApolloAndVuelidate(apollo, vulidate, 'input')

watch(show, () => vulidate.resetAll())

const submit = async () => {
  try {
    vulidate.touchAll()

    if (!vulidate.error.value) {
      const orgWebhook = useFragment(WebhookFragment, webhook.value)

      const secret =
        isSecretHidden(input.secret) && isSecretHidden(orgWebhook?.secret) ? null : input.secret
      const clientSecret =
        isSecretHidden(input.clientSecret) && isSecretHidden(orgWebhook?.clientSecret)
          ? null
          : input.clientSecret

      const copy = { ...input }
      delete copy.clientSecret
      await updateWebhook({
        input: {
          ...copy,
          secret: secret ?? clientSecret,
        },
      })

      show.value = false
    }
  } catch (ex: any) {
    new NetworkError({ ex }).dispatch()
  }
}
</script>

<style scoped lang="scss"></style>
